/**
 * Investment Analysis — Pure Functions
 *
 * NPV, payback, and cash flow calculations for heating system replacement.
 * Based on PN-EN 15459 methodology adapted for Polish market.
 */

import type {
  FuelCostResult,
  InvestmentCostDefinition,
  InvestmentAnalysisResult,
  HeatingCostConfig,
} from '../types/heatingCost.types';

/**
 * Simple payback period in years.
 * Returns Infinity if savings are zero or negative.
 */
export function calculateSimplePayback(
  investmentCost: number,
  annualSavings: number
): number {
  if (investmentCost <= 0) return 0;
  if (annualSavings <= 0) return Infinity;
  return investmentCost / annualSavings;
}

/**
 * Net Present Value over given horizon.
 *
 * NPV = Σ_{t=1}^{N} [ (savings × (1+g)^t - maintenance) / (1+r)^t ] - investment
 */
export function calculateNPV(params: {
  investmentCost: number;
  annualSavings: number;
  annualMaintenanceCost: number;
  discountRate: number;
  fuelPriceGrowthRate: number;
  years: number;
}): number {
  const { investmentCost, annualSavings, annualMaintenanceCost, discountRate, fuelPriceGrowthRate, years } = params;

  let npv = -investmentCost;
  for (let t = 1; t <= years; t++) {
    const adjustedSavings = annualSavings * Math.pow(1 + fuelPriceGrowthRate, t);
    const netCashFlow = adjustedSavings - annualMaintenanceCost;
    npv += netCashFlow / Math.pow(1 + discountRate, t);
  }
  return npv;
}

/**
 * Cumulative cash flow projection (undiscounted).
 * Returns array of cumulative net savings for each year.
 * Year 1 value includes the initial investment (negative).
 */
export function calculateCashFlowProjection(params: {
  investmentCost: number;
  annualSavings: number;
  annualMaintenanceCost: number;
  fuelPriceGrowthRate: number;
  years: number;
}): number[] {
  const { investmentCost, annualSavings, annualMaintenanceCost, fuelPriceGrowthRate, years } = params;

  const cashFlows: number[] = [];
  let cumulative = -investmentCost;

  for (let t = 1; t <= years; t++) {
    const adjustedSavings = annualSavings * Math.pow(1 + fuelPriceGrowthRate, t);
    const netCashFlow = adjustedSavings - annualMaintenanceCost;
    cumulative += netCashFlow;
    cashFlows.push(cumulative);
  }

  return cashFlows;
}

/**
 * Full investment analysis comparing switching from one system to another.
 */
export function analyzeInvestment(
  fromResult: FuelCostResult,
  toResult: FuelCostResult,
  toInvestment: InvestmentCostDefinition,
  config: Pick<HeatingCostConfig, 'discountRate' | 'fuelPriceGrowthRate' | 'analysisYears'>
): InvestmentAnalysisResult {
  const investmentCost = toInvestment.installationCost.typical;
  const annualSavings = fromResult.annualCostGross - toResult.annualCostGross;
  const maintenanceCost = toInvestment.annualMaintenanceCost;

  const simplePaybackYears = calculateSimplePayback(investmentCost, annualSavings);

  const npvParams = {
    investmentCost,
    annualSavings,
    annualMaintenanceCost: maintenanceCost,
    discountRate: config.discountRate,
    fuelPriceGrowthRate: config.fuelPriceGrowthRate,
  };

  const npv10Year = calculateNPV({ ...npvParams, years: 10 });
  const npv15Year = calculateNPV({ ...npvParams, years: 15 });
  const npv20Year = calculateNPV({ ...npvParams, years: 20 });

  const cashFlows = calculateCashFlowProjection({
    investmentCost,
    annualSavings,
    annualMaintenanceCost: maintenanceCost,
    fuelPriceGrowthRate: config.fuelPriceGrowthRate,
    years: config.analysisYears,
  });

  // 10-year cumulative (undiscounted)
  const cumulativeSavings10Year = cashFlows.length >= 10 ? cashFlows[9] : cashFlows[cashFlows.length - 1];

  // Find break-even year (first year where cumulative >= 0)
  const breakEvenIdx = cashFlows.findIndex(v => v >= 0);
  const breakEvenYear = breakEvenIdx >= 0 ? breakEvenIdx + 1 : null;

  // Determine recommendation
  let recommendation: InvestmentAnalysisResult['recommendation'];
  let recommendationReason: string;

  if (annualSavings <= 0) {
    recommendation = 'not_recommended';
    recommendationReason = 'Brak oszczędności — zmiana systemu nie przyniesie korzyści finansowych.';
  } else if (simplePaybackYears <= 5) {
    recommendation = 'highly_recommended';
    recommendationReason = `Szybki zwrot inwestycji w ${simplePaybackYears.toFixed(1)} lat.`;
  } else if (simplePaybackYears <= 10) {
    recommendation = 'recommended';
    recommendationReason = `Zwrot inwestycji w ${simplePaybackYears.toFixed(1)} lat — opłacalna zmiana.`;
  } else if (simplePaybackYears <= 15) {
    recommendation = 'neutral';
    recommendationReason = `Długi okres zwrotu (${simplePaybackYears.toFixed(1)} lat) — rozważ inne opcje.`;
  } else {
    recommendation = 'not_recommended';
    recommendationReason = `Bardzo długi okres zwrotu (${simplePaybackYears.toFixed(0)} lat) — nieopłacalne finansowo.`;
  }

  return {
    fromVariant: fromResult.variant.id,
    toVariant: toResult.variant.id,
    npvAnalysis: {
      investmentCost,
      annualSavings,
      simplePaybackYears,
      npv10Year,
      npv15Year,
      npv20Year,
      cumulativeSavings10Year,
      annualCashFlows: cashFlows,
      breakEvenYear,
      discountRate: config.discountRate,
      fuelPriceGrowthRate: config.fuelPriceGrowthRate,
    },
    recommendation,
    recommendationReason,
  };
}
